import type { ApplicationRegister } from '/@/types/application-register';
import type { ApplicationReport, ApplicationReportResult } from '/@/types/application-report';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { getCropState } from './components/timeline';

// Grouping of the seguimiento's applications into cards. Plain functions (no React), shared by the
// seguimiento timeline and the visit report (creation, detail and PDF).

export type ApplicationGroup = [ApplicationRegister, ...ApplicationRegister[]];

/**
 * Group a field's application registers into cards. Cards are split by product type, then
 * by date: by default one card per application_date; for corn, one card per
 * crop-monitoring state — so each (type, state) shows at most one application card (with
 * multiple product rows). Applications of different types never share a card.
 */
export const groupApplications = (
    registers: ApplicationRegister[],
    cropMonitoring?: CropMonitoring
): ApplicationGroup[] => {
    const byState = cropMonitoring?.crop === 'corn';
    const groups = new Map<string, ApplicationGroup>();
    for (const register of registers) {
        const dateKey = byState
            ? (getCropState(register.applicationDate, cropMonitoring)?.code ?? register.applicationDate)
            : register.applicationDate;
        const key = `${register.type ?? 'none'}|${dateKey}`;
        const group = groups.get(key);
        if (group) group.push(register);
        else groups.set(key, [register]);
    }
    return [...groups.values()];
};

export type ApplicationReportResultGroup = [ApplicationReportResult, ...ApplicationReportResult[]];
/** A report narrowed to the results of a single applicationName — never empty. */
export type ApplicationReportGroup = Omit<ApplicationReport, 'results'> & { results: ApplicationReportResultGroup };

/**
 * Split a FieldView application report into one card per applicationName. A report can
 * cover several distinct applications, and the card titles itself from the name of its
 * first result — so an ungrouped report shows every product it contains under whichever
 * application happened to come first. Insertion order is preserved, so the caller's
 * chronological placement still holds.
 */
export const groupReportByApplicationName = (report: ApplicationReport): ApplicationReportGroup[] => {
    const groups = new Map<string, ApplicationReportResultGroup>();
    for (const result of report.results) {
        const group = groups.get(result.applicationName);
        if (group) group.push(result);
        else groups.set(result.applicationName, [result]);
    }
    return [...groups.values()].map((results) => ({ ...report, results }));
};
